import React from 'react';
import DialogTitle from '../../DialogTitle';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { IonicButton, IonicInput } from 'ionic-ui';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { updateProduct } from '../../../../services/Products/updateProduct';
import useIonicToast from '../../../../hooks/useIonicToast';
import { useDialog } from '../../../../context/Dialog';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  id: number; //Product id
  productName: string;
};

export default function ProductRestockDialog({ id, productName }: Props) {
  const schema = z.object({
    quantity: z
      .number({ message: 'Quantity is required' })
      .min(1, { message: 'Quantity as to be greater that 0' }),
  });
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
  });
  const {
    userSession: { signedAccessToken: authToken },
    userStore: { activeStore },
  } = useSelector((state: RootState) => ({
    userSession: state.userSession,
    userStore: state.userStores,
  }));
  const storeId = activeStore?.id;
  const { presentIonToast } = useIonicToast();
  const { closeDialog, setBusy } = useDialog();
  const queryClient = useQueryClient();
  const handleCatalogInvalidation = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['products-catalog'],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ['products-metrics'],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ['products-inventory'],
      exact: false,
    });
  };
  const onSubmit = handleSubmit(async (fieldState) => {
    if (!authToken || !storeId) return;
    try {
      setBusy(true);
      await updateProduct(id, fieldState, authToken, storeId);
      presentIonToast(`${productName} successfully restocked.`);
      await handleCatalogInvalidation();
      closeDialog();
    } catch (error: any) {
      presentIonToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  });

  return (
    <div className="space-y-4 sm:max-w-[480px] dialog-content">
      <>
        <DialogTitle>Restock {productName}</DialogTitle>
        <p className="medium-emphasis-text">
          Enter the new quantity of product you have in stock.
        </p>
      </>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* STOCK QUANTITY */}
        <Controller
          name="quantity"
          control={control}
          render={({ field, fieldState, formState }) => (
            <>
              <IonicInput
                {...field}
                type="number"
                containerClass="quantity-counter"
                placeholder="0"
                min={0}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(Number(event.target.value))
                }
                hint={fieldState.error?.message}
                hintClass="text-red-600"
              />
              <IonicButton
                type="submit"
                className="w-full"
                disabled={!formState.isValid}
              >
                Submit
              </IonicButton>
            </>
          )}
        />
      </form>
    </div>
  );
}
